import {
  AwardType,
  DirtyFieldsType,
  GetConfigureFormOperations,
  Operation,
} from '@occc-app/offerHelpers/types';
import {
  getAwardRestrictionsOperations,
  getFulfillmentChannelConstraintOperations,
  getFulfillmentWindowOperations,
  getMinimumPurchaseConstraintOperations,
  getPaymentMethodConstraint,
  getPaymentMethodConstraintOperations,
  makeRequiresActivationConstraintOperations,
} from '@occc/feat-constraints';
import {
  findProgressPerTransactionLimit,
  getLimitsInConstraintOperations,
  getProgressPerPeriodLimitOperations,
  getProgressPerTransactionLimitOperations,
  LimitType,
} from '@occc/feat-limits';
import { ChallengeType } from '@occc-app/routes/Challenge/constants';
import { createOperationToDisableTriggerOnProgress } from '@occc-app/routes/CampaignComponents/components/TriggerOnProgress/utils';
import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import { OfferFragment } from '@occc/graphql';

import { getOfferNameAndDescriptionUpdateOperations } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionUpdateOperations';
import { getOfferFulfillmentPriorityUpdateOperations } from '@occc-app/offerHelpers/operations/getOfferFulfillmentPriorityUpdateOperations';
import { getScanDataConfigurationOperation } from '@occc-app/offerHelpers/operations/getScanDataConfigurationOperation';
import { getEarningMechanismOperations } from './earningMechanismOperations';
import {
  getIsProgressPerPeriodLimitSupported,
  getIsProgressPerTransactionLimitSupported,
  getIsMinimumPurchaseConstraintSupported,
  getIsTransactionLimitSupported,
} from './challenge.utils';
import { ChallengeConfigureFormValues } from '../../../types';
import { getOfferCustomDataOperation } from '../../../../../offerHelpers/operations/getOfferCustomDataOperation';

const getProgressPerPeriodOperations = (
  values: ChallengeConfigureFormValues,
  offerId: string,
  dirtyFields: DirtyFieldsType,
) => {
  const isProgressPerPeriodLimitSupported = getIsProgressPerPeriodLimitSupported(
    values.challengeType,
  );

  if (!isProgressPerPeriodLimitSupported && values.awardCount.progressPerPeriod?.id) {
    // Progress per period limit should be removed if we change type to Product Challenge
    return [makeDeleteOperation('limitDelete', values.awardCount.progressPerPeriod.id)];
  }

  if (!isProgressPerPeriodLimitSupported) {
    return [];
  }

  return getProgressPerPeriodLimitOperations(offerId, values.awardCount, dirtyFields);
};

const getProgressPerTransactionOperations = (
  offerId: string,
  offer: OfferFragment | undefined,
  values: ChallengeConfigureFormValues,
  dirtyFields: DirtyFieldsType,
): Operation[] => {
  const isProgressPerTransactionLimitSupported = getIsProgressPerTransactionLimitSupported(
    values.challengeType,
    values.productChallenge?.earningMechanism,
  );
  const progressPerTransactionLimit = offer
    ? findProgressPerTransactionLimit(offer.limits)
    : undefined;

  if (!isProgressPerTransactionLimitSupported && progressPerTransactionLimit) {
    return [makeDeleteOperation('limitDelete', progressPerTransactionLimit.id)];
  }

  if (!isProgressPerTransactionLimitSupported) {
    return [];
  }

  return getProgressPerTransactionLimitOperations(offerId, values.awardCount, dirtyFields);
};

export const getConfigureFormOperations: GetConfigureFormOperations<
  ChallengeConfigureFormValues
> = (offerId, offer, values, dirtyFields) => {
  const earningMechanismOperations = getEarningMechanismOperations(offerId, values, dirtyFields);

  // using delete and create instead of update
  const activationConstraintOperations = makeRequiresActivationConstraintOperations(
    offerId,
    values.activation,
    dirtyFields,
    offer,
  );
  const activationLimitsOperations = getLimitsInConstraintOperations(
    offerId,
    values.activation,
    dirtyFields,
    LimitType.Activation,
  );

  const isTransactionLimitSupported = getIsTransactionLimitSupported(
    values.challengeType,
    values.productChallenge?.earningMechanism,
  );

  const options = {
    isTransactionLimitSupported,
    ...(offer ? { isTransactionFrequencyLimitSupported: false, offer } : {}),
  };

  const awardCountLimitsOperations = getLimitsInConstraintOperations(
    offerId,
    values.awardCount,
    dirtyFields,
    LimitType.AwardCount,
    options,
  );

  const fulfillmentWindowOperations = getFulfillmentWindowOperations(
    offerId,
    values.fulfillmentWindow,
    dirtyFields,
  );

  const progressPerPeriodLimitOperations: Operation[] = getProgressPerPeriodOperations(
    values,
    offerId,
    dirtyFields,
  );

  const progressPerTransactionLimitOperations: Operation[] = getProgressPerTransactionOperations(
    offerId,
    offer,
    values,
    dirtyFields,
  );

  const supportsPaymentMethodConstraint = values.challengeType === ChallengeType.ProductChallenge;
  const paymentMethodConstraintOperations = supportsPaymentMethodConstraint
    ? getPaymentMethodConstraintOperations(offerId, offer, values, dirtyFields)
    : [];

  const fulfillmentChannelConstraintOperations = getFulfillmentChannelConstraintOperations(
    offerId,
    offer,
    values,
  );

  const isMinimumPurchaseConstraintSupported = getIsMinimumPurchaseConstraintSupported(
    values.challengeType,
    values.productChallenge?.earningMechanism,
  );

  const minimumPurchaseConstraintOperations = isMinimumPurchaseConstraintSupported
    ? getMinimumPurchaseConstraintOperations(offerId, values, dirtyFields, offer)
    : getMinimumPurchaseConstraintOperations(
        offerId,
        {
          minimumPurchaseConstraints: {
            isEnabled: false,
            items: [],
          },
        },
        dirtyFields,
        offer,
      );

  const additionalOperations: Operation[] = [];

  additionalOperations.push(
    ...getOfferNameAndDescriptionUpdateOperations(offerId, values, dirtyFields),
  );

  additionalOperations.push(
    ...getOfferFulfillmentPriorityUpdateOperations(offerId, values, dirtyFields),
  );

  const existingPaymentMethodConstraint = getPaymentMethodConstraint(offer);
  if (existingPaymentMethodConstraint && !supportsPaymentMethodConstraint) {
    // https://stuzo.atlassian.net/browse/OCA-1737
    // payment method constraint should be removed if we change type to Visit Challenge which
    // doesn't support this constraint
    additionalOperations.push(
      makeDeleteOperation('constraintDelete', existingPaymentMethodConstraint.id),
    );
  }

  const isOverTimeEarningMechanism =
    values.challengeType === ChallengeType.ProductChallenge &&
    values.productChallenge &&
    values.productChallenge.earningMechanism === 'varietyPurchaseOverTime';

  // https://stuzo.atlassian.net/browse/OCA-1516
  // when an offer subtype is changed to a non-Progress EM
  // set "triggerOnProgress" to "false" if needed
  if (
    offer?.earningMechanism?.__typename === 'EarningMechanismVarietyPurchaseOverTime' &&
    !isOverTimeEarningMechanism &&
    offer.triggerPreferences.triggerOnProgress
  ) {
    const operation = createOperationToDisableTriggerOnProgress(offerId, offer.triggerPreferences);
    additionalOperations.push(operation);
  }

  const awardRestrictionsOperations = offer
    ? getAwardRestrictionsOperations({
        offer,
        values: values.awardRestrictions,
        dirtyFields,
        forceDelete: values.award?.awardType !== AwardType.AwardVouchers,
      })
    : [];

  const customDataOperation = getOfferCustomDataOperation({
    dirtyFields,
    values,
    offerId,
  });
  if (customDataOperation) {
    additionalOperations.push(customDataOperation);
  }

  const scanDataConfigurationOperation = getScanDataConfigurationOperation({
    offerId,
    values,
    dirtyFields,
  });
  if (scanDataConfigurationOperation) {
    additionalOperations.push(scanDataConfigurationOperation);
  }

  // ORDER is important
  // Can not mix awardCount limits and constraint

  return values.activation.isEnabled
    ? [
        ...additionalOperations,
        ...earningMechanismOperations,
        ...fulfillmentWindowOperations,
        ...minimumPurchaseConstraintOperations,
        ...awardCountLimitsOperations,
        ...progressPerPeriodLimitOperations,
        ...progressPerTransactionLimitOperations,
        ...activationConstraintOperations,
        ...activationLimitsOperations,
        ...paymentMethodConstraintOperations,
        ...fulfillmentChannelConstraintOperations,
        ...awardRestrictionsOperations,
      ]
    : [
        ...additionalOperations,
        ...earningMechanismOperations,
        ...fulfillmentWindowOperations,
        ...minimumPurchaseConstraintOperations,
        ...activationConstraintOperations,
        ...activationLimitsOperations,
        ...awardCountLimitsOperations,
        ...progressPerPeriodLimitOperations,
        ...progressPerTransactionLimitOperations,
        ...paymentMethodConstraintOperations,
        ...fulfillmentChannelConstraintOperations,
        ...awardRestrictionsOperations,
      ];
};
